// UTM Parameter Utility Functions

/**
 * Get current timestamp for UTM tracking
 * @returns {number} Current timestamp in milliseconds
 */
export function getUTMTimestamp() {
  return Date.now();
}

/**
 * Extract UTM parameters from URL
 * @param {string} url - URL to parse (defaults to current window.location.href)
 * @returns {Object} Object containing UTM parameters
 */
export function extractUTMParams(url = null) {
  const currentUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const urlObj = new URL(currentUrl);
  
  const utmParams = {};
  const utmKeys = [
    'utm_source',
    'utm_medium', 
    'utm_campaign',
    'utm_term',
    'utm_content',
    'utm_id'
  ];
  
  utmKeys.forEach(key => {
    const value = urlObj.searchParams.get(key);
    if (value) {
      utmParams[key] = value;
    }
  });
  
  return utmParams;
}

/**
 * Save UTM parameters to sessionStorage
 * @param {Object} utmParams - UTM parameters object
 */
export function saveUTMToSession(utmParams) {
  if (typeof window === 'undefined') return;
  
  try {
    // Only store if at least one parameter is non-empty
    const hasUtmParams = Object.values(utmParams).some(value => value !== '');
    if (hasUtmParams) {
      sessionStorage.setItem('utmParams', JSON.stringify(utmParams));
      sessionStorage.setItem('utm_timestamp', Date.now().toString());
      console.log('UTM Parameters stored:', utmParams);
    }
  } catch (error) {
    console.error('Error saving UTM to session:', error);
  }
}

/**
 * Get UTM parameters from sessionStorage
 * @returns {Object} Object containing UTM parameters
 */
export function getUTMFromSession() {
  if (typeof window === 'undefined') return {};
  
  try {
    const utmData = sessionStorage.getItem('utmParams');
    if (utmData) {
      return JSON.parse(utmData);
    }
    
    return {};
  } catch (error) {
    console.error('Error getting UTM from session:', error);
    return {};
  }
}

/**
 * Check if UTM parameters exist in session
 * @returns {boolean} True if UTM parameters exist
 */
export function hasUTMParams() {
  if (typeof window === 'undefined') return false;
  
  const utmData = sessionStorage.getItem('utmParams');
  return !!utmData;
}

/**
 * Get UTM timestamp from session
 * @returns {number|null} Timestamp when UTM was captured
 */
export function getUTMTimestampFromSession() {
  if (typeof window === 'undefined') return null;
  
  const timestamp = sessionStorage.getItem('utm_timestamp');
  return timestamp ? parseInt(timestamp) : null;
}

/**
 * Clear UTM parameters from session
 */
export function clearUTMFromSession() {
  if (typeof window === 'undefined') return;
  
  try {
    sessionStorage.removeItem('utmParams');
    sessionStorage.removeItem('utm_timestamp');
    
    console.log('UTM parameters cleared from session');
  } catch (error) {
    console.error('Error clearing UTM from session:', error);
  }
}

/**
 * Check if UTM parameters are fresh (within specified time)
 * @param {number} maxAge - Maximum age in milliseconds (default: 30 minutes)
 * @returns {boolean} True if UTM parameters are fresh
 */
export function isUTMFresh(maxAge = 30 * 60 * 1000) {
  const timestamp = getUTMTimestampFromSession();
  if (!timestamp) return false;
  
  const now = Date.now();
  const age = now - timestamp;
  
  return age <= maxAge;
}

/**
 * Get UTM parameters with freshness check
 * @param {number} maxAge - Maximum age in milliseconds (default: 30 minutes)
 * @returns {Object} UTM parameters if fresh, empty object if stale
 */
export function getFreshUTMParams(maxAge = 30 * 60 * 1000) {
  if (isUTMFresh(maxAge)) {
    return getUTMFromSession();
  }
  
  // Clear stale UTM data
  clearUTMFromSession();
  return {};
}
